import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Chip,
  MenuItem,
  Tooltip,
} from "@mui/material";
import { Add, Delete, Close } from "@mui/icons-material";
import ExternalServiceIcon from "./externalServiceIcon";
import ExternalServiceAutocomplete from "./externalServiceAutocomplete.component";
import { useAttachmentRemovals } from "../../hooks/useAttachmentRemovals";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import { useSnackbar } from "notistack";

import axios from "../../api/axios";
import { useCRUD } from "../../api/crud";
import { useAuthContext, useHasPermission, useIsServiceRole } from "../../contexts/auth/auth.hooks";
import CustomCircularProgress from "../ui/customCircularProgress/customCircularProgress.component";
import FileDropZoneField from "../ui/fileDropZoneField/fileDropZoneField.component";
import CustomDatePicker from "../ui/customDatePicker/customDatePicker.component";
import EquipmentAutocomplete from "../ui/equipmentAutocomplete/equipmentAutocomplete.component";
import { SelectedEquipmentDetails } from "./selectedEquipmentDetails.component";
import TaskServicesField, {
  inProgressHours,
  TaskServiceOption,
  toServiceQuantities,
  toTaskServiceOptions,
} from "./taskServicesField.component";
import CustomModal from "../ui/modal/modal.component";
import CustomAutocomplete, { OptionRecord } from "../ui/customAutocomplete/customAutocomplete.component";
import AddClientModal from "../clientForm/addClientModal.component";
import AddNumerationSchemaModal from "../ui/addNumerationSchemaModal/addNumerationSchemaModal.component";
import JobIssueForm from "./jobIssueForm.component";
import {
  CLIENTS_ENDPOINT,
  CONTRACT_TERMS_ENDPOINT,
  EQUIPMENTS_ENDPOINT,
  JOBS_ENDPOINT,
  JOB_ISSUES_ENDPOINT,
  JOB_KIND_OPTIONS,
  JOB_STATUS_OPTIONS,
  NUMERATION_SCHEMA_ENDPOINT,
  TENANT_SETTINGS_ENDPOINT,
  USERS_ENDPOINT,
  ATTACHMENTS_ENDPOINT,
  ERROR_SNACKBAR,
  SUCCESS_SNACKBAR,
} from "../../consts";
import styles from "./jobForm.module.css";
import { formatAddress, highlightMatch } from "../../utils";
import { AttachmentOrigin, Job, JobStatusTimeline, JobTask } from "../../types/jobs";
import { Equipment } from "../../types/equipments";

// ============================================================================
// Form Types & Constants
// ============================================================================

type AttachmentChip = {
  id: number;
  label: string;
  url?: string;
  origin?: AttachmentOrigin;
  // True once the file is bound to a task server-side. Removing one of those
  // is only carried out when the form is saved - see useAttachmentRemovals.
  persisted?: boolean;
};

type JobTaskRow = {
  id?: number;
  equipment: string | number | null;
  issues: OptionRecord[];
  services: TaskServiceOption[];
  description: string;
  // Files only ever describe the machine they were taken of, so they belong
  // to the task rather than to the job as a whole.
  attachments: AttachmentChip[];
};

type JobFormValues = {
  kind: string;
  status: string;
  numeration_schema_id: OptionRecord | null;
  number: string;
  planned_for: string | null;
  assigned_to: OptionRecord[];
  // "External service" picked in Assigned to - the job is handed over to an
  // outside company, chosen in the select that then appears. Technicians can
  // stay on the job next to it.
  external_service_selected: boolean;
  external_service: OptionRecord | null;
  client_id: string | number | null;
  complaint_for_id: string | number | null;
  description: string;
  tasks: JobTaskRow[];
};

// Stands for "hand the job over to an external service" among the technicians
// in Assigned to - never sent as an assignee.
const EXTERNAL_SERVICE_OPTION_ID = "__external_service__";
const isExternalServiceOption = (option: OptionRecord) => option.id === EXTERNAL_SERVICE_OPTION_ID;

// Utility Functions
const createDefaultTaskRow = (): JobTaskRow => ({
  equipment: null,
  issues: [],
  services: [],
  description: "",
  attachments: [],
});

const attachmentChipFrom = (
  row: { id: number; attachment?: string; origin?: AttachmentOrigin },
  fallbackLabel: string,
  persisted: boolean
): AttachmentChip => {
  const url = row.attachment || "";
  return {
    id: row.id,
    label: url ? url.replace(/^.*\//, "") : fallbackLabel,
    url,
    origin: row.origin ?? null,
    persisted,
  };
};

// dayjs `.day()` numbering - Sunday is 0 regardless of locale.
const SUNDAY = 0;
const SATURDAY = 6;

// Next Monday-Friday day after today - a job created on Friday (or over the
// weekend) is planned for Monday rather than a day nobody works.
const getNextBusinessDay = (): Dayjs => {
  let day = dayjs().add(1, "day");
  while (day.day() === SUNDAY || day.day() === SATURDAY) {
    day = day.add(1, "day");
  }
  return day;
};

const buildDefaultFormValues = (defaultKind = "EWIC"): JobFormValues => ({
  kind: defaultKind,
  status: "",
  numeration_schema_id: null,
  number: "",
  planned_for: getNextBusinessDay().format("YYYY-MM-DD"),
  assigned_to: [],
  external_service_selected: false,
  external_service: null,
  client_id: null,
  complaint_for_id: null,
  description: "",
  tasks: [createDefaultTaskRow()],
});

const extractErrorMessage = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (Array.isArray(value) && value.length > 0) {
    const first = value[0];
    if (!first) return null;
    if (typeof first === "string") return first;
    if (typeof first === "object" && first && "message" in first) {
      return (first as { message?: string }).message ?? null;
    }
  }
  if (typeof value === "object" && value && "message" in value) {
    return (value as { message?: string }).message ?? null;
  }
  return null;
};

// ============================================================================
// Main Component
// ============================================================================

interface JobFormProps {
  jobId?: string | number;
  onSuccess?: (createdJob?: Job) => void;
  initialClientId?: string | number | null;
  initialEquipmentId?: string | number | null;
  initialComplaintForId?: string | number | null;
  // Restricts the rendered form to the Tasks section only (used by the
  // "Update tasks" action available to users without full job-edit rights).
  // The rest of the job's fields are still fetched, kept in state and
  // resubmitted unchanged, so nothing else on the job is affected.
  onlyTasks?: boolean;
}

export default function JobForm({
  jobId,
  onSuccess,
  initialClientId,
  initialEquipmentId,
  initialComplaintForId,
  onlyTasks = false,
}: JobFormProps) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuthContext();
  // A technician may add their own files but not take away the ones the job
  // was reported with - those are somebody else's account of the fault.
  const isServiceRole = useIsServiceRole();
  const canViewExternalServices = useHasPermission("service.view_externalservice");
  // Missing basic data can be added from the pickers, each with its own right.
  const canAddClient = useHasPermission("settlement.add_client");
  const canAddNumerationSchema = useHasPermission("settlement.add_numerationschema");
  const canAddJobIssue = useHasPermission("service.add_jobissue");
  const { create, update, get, loading } = useCRUD(JOBS_ENDPOINT);
  const attachmentRemovals = useAttachmentRemovals();
  const isEditMode = Boolean(jobId);
  const statusDefault = JOB_STATUS_OPTIONS[0]?.value || "";
  const canAddAnyJob = Boolean(currentUser?.permissions?.includes("service.add_job"));
  const canAddInternalJobOnly = Boolean(
    currentUser?.permissions?.includes("service.add_internal_job") && !canAddAnyJob
  );
  const defaultJobKind = !isEditMode && canAddInternalJobOnly ? "I" : "EWIC";

  // =========================================================================
  // State Management
  // =========================================================================

  const [formData, setFormData] = useState<JobFormValues>(() =>
    buildDefaultFormValues(defaultJobKind)
  );
  const [initializing, setInitializing] = useState(true);
  const [serverErrors, setServerErrors] = useState<any>(null);
  const [numberLoading, setNumberLoading] = useState(false);
  // Index of the task row whose drop zone is mid-upload, so only that row's
  // zone is disabled while a file is on its way.
  const [uploadingTaskIndex, setUploadingTaskIndex] = useState<number | null>(null);
  const [clientSearchInput, setClientSearchInput] = useState('');
  const [selectedClientOption, setSelectedClientOption] = useState<OptionRecord | null>(null);
  // Of the job being edited: whether it is finished (and invoiced) - editing
  // one takes "Edit finished job" and a confirmation - and how long it was in
  // progress, which the hourly services offer as their quantity.
  const [editedJobState, setEditedJobState] = useState<{
    finished: boolean;
    invoiced: boolean;
    timeline: JobStatusTimeline | null;
  }>({ finished: false, invoiced: false, timeline: null });
  const [confirmFinishedEditOpened, setConfirmFinishedEditOpened] = useState(false);
  const [addClientOpened, setAddClientOpened] = useState(false);
  const [addNumerationSchemaOpened, setAddNumerationSchemaOpened] = useState(false);
  // The task whose "Add new issue" is open - the new issue goes onto it.
  const [addIssueTaskIndex, setAddIssueTaskIndex] = useState<number | null>(null);

  // Consolidated autocomplete state
  const [autocompleteData, setAutocompleteData] = useState<{
    options: Record<string, OptionRecord[]>;
    loading: Record<string, boolean>;
  }>({
    options: {},
    loading: {},
  });

  // Refs
  const formDataRef = useRef(formData);
  const previousClientIdRef = useRef<string | number | null>(formData.client_id);
  const pendingClientLookupIdRef = useRef<string | number | null>(null);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // =========================================================================
  // Initialization Effect
  // =========================================================================

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      try {
        if (jobId) {
          const jobResponse = await get(jobId);
          if (cancelled) return;
          if (jobResponse?.data) {
            hydrateFormFromJob(jobResponse.data);
          }
        } else {
          const initialUpdates: Partial<JobFormValues> = {
            status: statusDefault,
          };

          if (initialClientId != null) {
            initialUpdates.client_id = initialClientId;
            previousClientIdRef.current = initialClientId;
            // Fetch client display name
            try {
              const { data } = await axios.get(`${CLIENTS_ENDPOINT}${initialClientId}/`);
              if (!cancelled && data) setSelectedClientOption(data);
            } catch {
              // client might not be found, silent fail
            }
          }

          if (initialEquipmentId != null) {
            initialUpdates.tasks = [{ ...createDefaultTaskRow(), equipment: initialEquipmentId }];
            // Fetch equipment for display in autocomplete
            try {
              const { data } = await axios.get(`${EQUIPMENTS_ENDPOINT}${initialEquipmentId}/`);
              if (!cancelled && data) {
                setAutocompleteData((prev) => ({
                  ...prev,
                  options: { ...prev.options, "tasks.equipment": [data] },
                }));
              }
            } catch {
              // equipment might not be found, silent fail
            }
          }

          let complaintPrefilled = false;
          if (initialComplaintForId != null) {
            initialUpdates.complaint_for_id = initialComplaintForId;
            try {
              const { data } = await axios.get(`${JOBS_ENDPOINT}${initialComplaintForId}/`);
              if (!cancelled && data) {
                applyComplaintTargetPrefill(data);
                complaintPrefilled = true;
              }
            } catch {
              // source job might not be found, silent fail
            }
          }

          if (!complaintPrefilled) {
            // Prefill the numeration schema configured as the workspace default for jobs
            try {
              const { data: tenantSettings } = await axios.get(`${TENANT_SETTINGS_ENDPOINT}settings/`);
              const defaultSchemaId = tenantSettings?.default_job_numeration_schema;
              if (!cancelled && defaultSchemaId) {
                const { data: schema } = await axios.get(`${NUMERATION_SCHEMA_ENDPOINT}${defaultSchemaId}/`);
                if (!cancelled && schema) {
                  initialUpdates.numeration_schema_id = schema;
                  setAutocompleteData((prev) => ({
                    ...prev,
                    options: { ...prev.options, numeration_schema_id: [schema] },
                  }));
                }
              }
            } catch {
              // tenant settings or default schema might not be found, silent fail
            }
          }

          if (!cancelled && !complaintPrefilled) {
            setFormData((prev) => ({ ...prev, ...initialUpdates }));
            if (initialUpdates.numeration_schema_id) {
              fetchNextNumber(initialUpdates.numeration_schema_id.id);
            }
          }
        }
      } catch {
        setFormData((prev) => ({
          ...prev,
          status: prev.status || statusDefault,
        }));
      } finally {
        if (!cancelled) setInitializing(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // =========================================================================
  // Client Change Effect
  // =========================================================================

  const clientId = formData.client_id;
  useEffect(() => {
    if (initializing || previousClientIdRef.current === clientId) return;
    previousClientIdRef.current = clientId;

    // Clear equipment when client changes
    setFormData((prev) => ({
      ...prev,
      tasks: prev.tasks.map((row) => ({ ...row, equipment: null })),
    }));
    setAutocompleteData((prev) => ({
      ...prev,
      options: { ...prev.options, "tasks.equipment": [] },
    }));
  }, [clientId, initializing]);

  // =========================================================================
  // Helper Functions
  // =========================================================================

  function applyComplaintTargetPrefill(sourceJob: Job) {
    const rawTasks = sourceJob.tasks ?? sourceJob.services ?? [];
    const tasks: JobTaskRow[] =
      Array.isArray(rawTasks) && rawTasks.length > 0
        ? rawTasks.map((task: JobTask) => ({
            equipment: task.equipment?.id ?? null,
            issues: (task.issues ?? []).map((issue) =>
              typeof issue === "object" ? issue : ({ id: issue } as OptionRecord)
            ),
            // Deliberately not copied: a complaint is a visit of its own, and
            // the technician records what it took - carrying over the first
            // visit's priced services would put them on the complaint's invoice,
            // and its task description says what was done then, not now.
            services: [],
            description: "",
            // Deliberately not copied: the files on the job being complained
            // about document that visit, not this new report.
            attachments: [],
          }))
        : [createDefaultTaskRow()];

    const hydratedClientId = sourceJob.client?.id ?? sourceJob.client_id ?? null;
    const numerationSchema = sourceJob.numeration_schema ?? null;
    previousClientIdRef.current = hydratedClientId;
    setSelectedClientOption(sourceJob.client ?? null);

    setFormData((prev) => ({
      ...prev,
      kind: "C",
      status: statusDefault,
      client_id: hydratedClientId,
      complaint_for_id: initialComplaintForId ?? sourceJob.id,
      numeration_schema_id: numerationSchema,
      // The job's own description - what the client reported - still applies
      // to the complaint; the tasks' descriptions of the work done do not.
      description: sourceJob.description ?? "",
      tasks,
    }));

    if (numerationSchema?.id) {
      fetchNextNumber(numerationSchema.id);
    }

    const equipmentOptions: OptionRecord[] = [];
    const equipmentIds = new Set<string | number>();
    rawTasks.forEach((task: JobTask) => {
      const equipment = task.equipment;
      if (equipment?.id != null && !equipmentIds.has(equipment.id)) {
        equipmentIds.add(equipment.id);
        equipmentOptions.push(equipment);
      }
    });

    const allIssues: OptionRecord[] = [];
    const issueIds = new Set<string | number>();
    rawTasks.forEach((task: JobTask) => {
      task.issues?.forEach((issue) => {
        const issueId = typeof issue === "object" ? issue.id : issue;
        if (issueId != null && !issueIds.has(issueId)) {
          issueIds.add(issueId);
          if (typeof issue === "object" && issue.id) allIssues.push(issue);
        }
      });
    });

    setAutocompleteData((prev) => ({
      ...prev,
      options: {
        ...prev.options,
        ...(numerationSchema ? { numeration_schema_id: [numerationSchema] } : {}),
        ...(equipmentOptions.length > 0 ? { "tasks.equipment": equipmentOptions } : {}),
        ...(allIssues.length > 0 ? { "tasks.issues": allIssues } : {}),
      },
    }));
  }

  function hydrateFormFromJob(job: Job) {
    const rawTasks = job.tasks ?? job.services ?? [];
    const tasks: JobTaskRow[] =
      Array.isArray(rawTasks) && rawTasks.length > 0
        ? rawTasks.map((task: any) => ({
            id: task.id,
            equipment: task.equipment?.id ?? task.equipment ?? null,
            issues: task.issues,
            services: toTaskServiceOptions(task.services),
            description: task.description ?? "",
            attachments: Array.isArray(task.attachments)
              ? task.attachments.map((attachment: { id: number; attachment?: string }) =>
                  attachmentChipFrom(attachment, t("Attachment"), true)
                )
              : [],
          }))
        : [createDefaultTaskRow()];

    const hydratedClientId = job.client?.id ?? job.client_id ?? null;
    previousClientIdRef.current = hydratedClientId;
    setSelectedClientOption(job.client ?? null);
    setEditedJobState({
      finished: job.status === "F",
      invoiced: Boolean(job.is_invoiced),
      timeline: job.status_timeline ?? null,
    });

    setFormData({
      kind: job.kind ?? "",
      status: job.status ?? statusDefault,
      numeration_schema_id: job.numeration_schema ?? null,
      number: job.number ?? "",
      planned_for: job.planned_for ?? null,
      assigned_to: job.assigned_to_details.map((detail) => ({
        id: detail.id,
        full_name: `${detail.first_name} ${detail.last_name} (${detail.email})`,
      })),
      external_service_selected: Boolean(job.external_service),
      external_service: job.external_service ?? null,
      client_id: hydratedClientId,
      complaint_for_id: job.complaint_for_id ?? null,
      description: job.description ?? "",
      tasks,
    });

    // Populate initial options from job data
    const newOptions: Record<string, OptionRecord[]> = {};
    if (job.numeration_schema) newOptions.numeration_schema_id = [job.numeration_schema];
    if (Array.isArray(job.assigned_to_details) && job.assigned_to_details.length > 0) {
      newOptions.assigned_to = job.assigned_to_details;
    }

    const allIssues: OptionRecord[] = [];
    const issueIds = new Set<string | number>();
    rawTasks.forEach((task: any) => {
      task.issues?.forEach((issue: any) => {
        const id = typeof issue === "object" ? issue.id : issue;
        if (id != null && !issueIds.has(id)) {
          issueIds.add(id);
          if (typeof issue === "object" && issue.id) allIssues.push(issue);
        }
      });
    });
    if (allIssues.length > 0) newOptions["tasks.issues"] = allIssues;

    const allTaskServices: OptionRecord[] = [];
    const serviceIds = new Set<string | number>();
    rawTasks.forEach((task: any) => {
      task.services?.forEach((svc: any) => {
        const id = typeof svc === "object" ? svc.id : svc;
        if (id != null && !serviceIds.has(id)) {
          serviceIds.add(id);
          if (typeof svc === "object" && svc.id) allTaskServices.push(svc);
        }
      });
    });
    if (allTaskServices.length > 0) newOptions["tasks.services"] = allTaskServices;

    const allEquipment: OptionRecord[] = [];
    const equipmentIds = new Set<string | number>();
    rawTasks.forEach((task: any) => {
      if (task.equipment && typeof task.equipment === "object" && task.equipment.id) {
        if (!equipmentIds.has(task.equipment.id)) {
          equipmentIds.add(task.equipment.id);
          allEquipment.push(task.equipment);
        }
      }
    });
    if (allEquipment.length > 0) newOptions["tasks.equipment"] = allEquipment;

    setAutocompleteData((prev) => ({
      ...prev,
      options: { ...prev.options, ...newOptions },
    }));
  }

  function buildClientValue(): OptionRecord | null {
    if (formData.client_id === null || formData.client_id === undefined || formData.client_id === "") {
      return null;
    }

    if (
      selectedClientOption?.id !== undefined &&
      String(selectedClientOption.id) === String(formData.client_id)
    ) {
      return selectedClientOption;
    }

    return { id: formData.client_id, name: String(formData.client_id) } as OptionRecord;
  }

  function getFieldError(field: keyof JobFormValues): string | null {
    return extractErrorMessage(serverErrors?.[field as string]);
  }

  // Sent as external_service_id, which isn't a key of the form's own values.
  const externalServiceError = extractErrorMessage(serverErrors?.external_service_id);

  function getServiceFieldError(
    rowIndex: number,
    field: keyof JobTaskRow | "service_quantities"
  ): string | null {
    if (!Array.isArray(serverErrors?.tasks)) return null;
    const rowErrors = serverErrors.tasks[rowIndex];
    const fieldErrors = rowErrors?.[field as string];
    // Quantities are a map, so their errors come keyed by the service id.
    if (field === "service_quantities" && fieldErrors && typeof fieldErrors === "object" && !Array.isArray(fieldErrors)) {
      return extractErrorMessage(Object.values(fieldErrors)[0]);
    }
    return fieldErrors ? extractErrorMessage(fieldErrors) : null;
  }

  async function fetchNextNumber(schemaId: string | number) {
    setNumberLoading(true);
    try {
      const { data } = await axios.get(`${NUMERATION_SCHEMA_ENDPOINT}${schemaId}/next/`);
      const nextValue = data?.value ?? data?.result ?? data?.number ?? data;
      setFormData((prev) => ({
        ...prev,
        number: nextValue ? String(nextValue) : "",
      }));
    } catch (err) {
      console.error("Failed to fetch next job number", err);
      enqueueSnackbar(t("Unable to fetch next number."), ERROR_SNACKBAR);
    } finally {
      setNumberLoading(false);
    }
  }

  function handleSubmit() {
    // A finished job is what its invoice was built from: saving it is asked
    // for twice, the second time in red.
    if (isEditMode && editedJobState.finished) {
      setConfirmFinishedEditOpened(true);
      return;
    }
    submitJob();
  }

  function submitJob() {
    setConfirmFinishedEditOpened(false);
    setServerErrors(null);
    if (formData.external_service_selected && !formData.external_service) {
      setServerErrors({ external_service_id: [t("Choose the external service the job is handed over to.")] });
      enqueueSnackbar(t("There was an error, please review the form."), ERROR_SNACKBAR);
      return;
    }
    const payload = {
      kind: formData.kind || undefined,
      status: formData.status || undefined,
      numeration_schema_id: formData.numeration_schema_id?.id || undefined,
      number: formData.number || undefined,
      planned_for: formData.planned_for || undefined,
      assigned_to: formData.assigned_to.map(i => i.id),
      // Left out for someone who can't see external services, so saving the
      // form never takes a job back from one behind their back.
      ...(canViewExternalServices
        ? {
            external_service_id: formData.external_service_selected
              ? formData.external_service?.id ?? null
              : null,
          }
        : {}),
      client_id: formData.client_id || undefined,
      complaint_for_id: !isEditMode && formData.complaint_for_id ? formData.complaint_for_id : undefined,
      description: formData.description || undefined,
      tasks: formData.tasks.map((task) => ({
        id: task.id,
        equipment: task.equipment || undefined,
        issues: task.issues.map(i => i.id),
        services: task.services.map(i => i.id),
        service_quantities: toServiceQuantities(task.services),
        description: task.description || undefined,
        attachment_ids:
          task.attachments.length > 0 ? task.attachments.map((c) => c.id) : undefined,
      })),
    };

    const request = isEditMode
      ? update({ id: jobId, ...payload })
      : create(payload);

    request.then(async (response) => {
      if (response?.success) {
        // Only now are the files the user took off the form actually deleted:
        // up to this point closing the form would have left them untouched.
        const removalsApplied = await attachmentRemovals.commit();
        if (!removalsApplied) {
          enqueueSnackbar(t("Failed to remove attachment."), ERROR_SNACKBAR);
        }

        enqueueSnackbar(t("Form was succesfully processed."), SUCCESS_SNACKBAR);
        if (!isEditMode) {
          setFormData((prev) => ({
            ...buildDefaultFormValues(defaultJobKind),
            status: statusDefault || prev.status,
          }));
          setSelectedClientOption(null);
          setAutocompleteData({ options: {}, loading: {} });
        }
        setServerErrors(null);
        onSuccess?.(isEditMode ? undefined : (response?.data as Job | undefined));
      } else {
        const errorData = response?.errorData;
        setServerErrors(errorData);
        const failureMessage =
          extractErrorMessage(errorData?.message) ||
          extractErrorMessage(errorData?.detail) ||
          t("There was an error, please review the form.");
        enqueueSnackbar(failureMessage, ERROR_SNACKBAR);
      }
    });
  }

  function handleTaskFieldChange(
    index: number,
    field: keyof JobTaskRow,
    value: OptionRecord[] | Equipment | string | number | null | (string | number)[]
  ) {
    setFormData((prev) => {
      const tasks = [...prev.tasks];
      tasks[index] = { ...tasks[index], [field]: value } as JobTaskRow;
      return { ...prev, tasks };
    });
  }

  function selectClient(client: OptionRecord | null) {
    setSelectedClientOption(client);
    setFormData((prev) => ({ ...prev, client_id: client ? client.id : null }));
  }

  function selectNumerationSchema(schema: OptionRecord | null) {
    setFormData((prev) => ({
      ...prev,
      numeration_schema_id: schema,
      number: schema ? prev.number : "",
    }));
    if (schema) fetchNextNumber(schema.id);
  }

  // An issue just added to the catalogue goes straight onto the task it was
  // added from.
  function handleIssueCreated(issue?: OptionRecord) {
    const taskIndex = addIssueTaskIndex;
    setAddIssueTaskIndex(null);
    if (!issue || taskIndex === null) return;
    setFormData((prev) => {
      const task = prev.tasks[taskIndex];
      if (!task || task.issues.some((picked) => String(picked.id) === String(issue.id))) return prev;
      const tasks = [...prev.tasks];
      tasks[taskIndex] = { ...task, issues: [...task.issues, issue] };
      return { ...prev, tasks };
    });
  }

  function addTaskRow() {
    setFormData((prev) => ({
      ...prev,
      tasks: [...prev.tasks, createDefaultTaskRow()],
    }));
  }

  function removeTaskRow(index: number) {
    setFormData((prev) => {
      if (prev.tasks.length === 1) {
        return { ...prev, tasks: [createDefaultTaskRow()] };
      }
      return {
        ...prev,
        tasks: prev.tasks.filter((_, i) => i !== index),
      };
    });
  }

  // The file is uploaded the moment it is picked, which leaves an attachment
  // owned by nothing; saving the job is what binds it to the task it was
  // dropped on. Rows are addressed by index because a task has no id until it
  // is saved.
  async function handleTaskAttachmentFile(index: number, file: File) {
    if (!file) return;
    setUploadingTaskIndex(index);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("attachment", file);
      const { data } = await axios.post(ATTACHMENTS_ENDPOINT, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.id != null) {
        const chip = attachmentChipFrom(data, file.name, false);
        setFormData((prev) => {
          const tasks = [...prev.tasks];
          tasks[index] = {
            ...tasks[index],
            attachments: [...tasks[index].attachments, chip],
          };
          return { ...prev, tasks };
        });
      }
    } catch (err) {
      console.error("Attachment upload failed", err);
      enqueueSnackbar(t("Failed to upload file."), ERROR_SNACKBAR);
    } finally {
      setUploadingTaskIndex(null);
    }
  }

  // A file that is already on the task is only taken off the list here; it is
  // deleted once the form is saved, so closing the form instead puts it back.
  async function handleRemoveTaskAttachment(index: number, chip: AttachmentChip) {
    const removed = await attachmentRemovals.remove(chip.id, Boolean(chip.persisted));
    if (!removed) {
      enqueueSnackbar(t("Failed to remove attachment."), ERROR_SNACKBAR);
      return;
    }

    setFormData((prev) => {
      const tasks = [...prev.tasks];
      tasks[index] = {
        ...tasks[index],
        attachments: tasks[index].attachments.filter((c) => c.id !== chip.id),
      };
      return { ...prev, tasks };
    });
  }

  // =========================================================================
  // Layout & Styling
  // =========================================================================
  // Styles are defined in jobForm.module.css and imported as CSS module

  // =========================================================================
  // Memoized Values
  // =========================================================================

  const generalError = useMemo(() => {
    if (!serverErrors) return null;
    return (
      extractErrorMessage(serverErrors?.message) ||
      extractErrorMessage(serverErrors?.detail) ||
      extractErrorMessage(serverErrors?.non_field_errors)
    );
  }, [serverErrors]);

  const availableJobKindOptions = useMemo(() => {
    if (isEditMode || !canAddInternalJobOnly) {
      return JOB_KIND_OPTIONS;
    }

    return JOB_KIND_OPTIONS.filter((option) => option.value === "I");
  }, [canAddInternalJobOnly, isEditMode]);

  const isNumberDisabled = Boolean(formData.numeration_schema_id);

  const externalServiceOption = useMemo<OptionRecord>(
    () => ({ id: EXTERNAL_SERVICE_OPTION_ID, full_name: t("External service") }),
    [t]
  );

  // The job's machines, so the list of external services can put the ones
  // working at the job's address first (see ExternalServiceViewSet).
  const taskEquipmentIds = formData.tasks
    .map((task) => task.equipment)
    .filter((equipmentId) => equipmentId !== null && equipmentId !== undefined && equipmentId !== "")
    .join(",");

  useEffect(() => {
    if (isEditMode || !canAddInternalJobOnly) {
      return;
    }

    setFormData((prev) => {
      if (prev.kind === "I" && prev.complaint_for_id == null) {
        return prev;
      }

      return {
        ...prev,
        kind: "I",
        complaint_for_id: null,
      };
    });
  }, [canAddInternalJobOnly, isEditMode]);

  if (initializing) {
    return <CustomCircularProgress />;
  }

  // =========================================================================
  // Render
  // =========================================================================

  const finishedJobWarning = editedJobState.invoiced
    ? t(
        "This job is finished and invoiced. Changes will not update the invoice already created from it, but they will change the stock, statistics and profitability."
      )
    : t(
        "This job is finished. Changes will change the stock, statistics and profitability, and an invoice created from it later will bill the job as you leave it."
      );

  return (
    <Box className={styles.formContainer}>
      {isEditMode && editedJobState.finished && (
        <Alert severity="error">{finishedJobWarning}</Alert>
      )}
      {generalError && (
        <Typography variant="body2" className={styles.errorText}>
          {generalError}
        </Typography>
      )}

      {/* Client */}
      {!onlyTasks && (
      <Box>
        <CustomAutocomplete
          size="small"
          apiConfig={{
            url: `${CLIENTS_ENDPOINT}for-job-form/`,
          }}
          value={buildClientValue()}
          onInputChange={(_, input, reason) => {
            if (reason === "input") {
              setClientSearchInput(input);
            }
          }}
          onChange={(_, newValue) => selectClient(newValue ?? null)}
          addNew={
            canAddClient
              ? { label: t("Add new client"), onClick: () => setAddClientOpened(true) }
              : null
          }
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("Client")}
              error={Boolean(getFieldError("client_id"))}
              helperText={getFieldError("client_id") || ""}
              className={styles.inputBase}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            const address = formatAddress(option.address || null);
            return (
              <Box component="li" key={key} {...otherProps} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'center' }}>
                  {highlightMatch(option.name, clientSearchInput)}
                </Typography>
                {address && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, textAlign: 'center' }}>
                    {highlightMatch(address, clientSearchInput)}
                  </Typography>
                )}
              </Box>
            );
          }}
        />
        {/* Next to its picker: this Box is a plain block, so the closed
            modal's wrapper takes no room in the form's gapped column. */}
        <AddClientModal
          opened={addClientOpened}
          setOpened={setAddClientOpened}
          onCreated={selectClient}
        />
      </Box>
      )}

      {!onlyTasks && (
      <>
      {/* Numeration Schema & Number */}
      <Box className={styles.formRow}>
        <Box className={styles.field45}>
          <CustomAutocomplete
            apiConfig={{
              url: NUMERATION_SCHEMA_ENDPOINT,
            }}
            size="small"
            value={formData.numeration_schema_id}
            onChange={(_, newValue) => selectNumerationSchema(newValue)}
            addNew={
              canAddNumerationSchema
                ? { label: t("Add numeration schema"), onClick: () => setAddNumerationSchemaOpened(true) }
                : null
            }
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Numeration schema")}
                error={Boolean(getFieldError("numeration_schema_id"))}
                helperText={getFieldError("numeration_schema_id") || ""}
                className={styles.inputBase}
              />
            )}
          />
          <AddNumerationSchemaModal
            opened={addNumerationSchemaOpened}
            setOpened={setAddNumerationSchemaOpened}
            onCreated={selectNumerationSchema}
          />
        </Box>

        <Box className={styles.field45}>
          <TextField
            size="small"
            label={t("Number")}
            value={formData.number}
            onChange={(e) => setFormData((prev) => ({ ...prev, number: e.target.value }))}
            fullWidth
            disabled={isNumberDisabled || numberLoading}
            InputProps={{
              endAdornment: numberLoading ? (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {t("Loading...")}
                </Typography>
              ) : undefined,
            }}
            error={Boolean(getFieldError("number"))}
            helperText={getFieldError("number") || ""}
            className={styles.inputBase}
          />
        </Box>
      </Box>

      {/* Kind & Planned for */}
      <Box className={styles.formRow}>
        <Box className={styles.field45}>
          <TextField
            select
            size="small"
            label={t("Kind")}
            value={formData.kind}
            onChange={(e) => setFormData((prev) => ({ ...prev, kind: e.target.value }))}
            fullWidth
            error={Boolean(getFieldError("kind"))}
            helperText={getFieldError("kind") || ""}
            className={styles.inputBase}
          >
              {availableJobKindOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {t(option.labelKey)}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <Box className={styles.field45}>
          <CustomDatePicker
            label={t("Planned for")}
            value={formData.planned_for ? dayjs(formData.planned_for) : null}
            onChange={(value: Dayjs | null) =>
              setFormData((prev) => ({
                ...prev,
                planned_for: value ? value.format("YYYY-MM-DD") : null,
              }))
            }
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                error: Boolean(getFieldError("planned_for")),
                helperText: getFieldError("planned_for") || "",
                className: styles.inputBase,
              },
            }}
          />
        </Box>
      </Box>

      {/* Assigned To */}
      <Box className={styles.formRow}>
        <Box className={styles.field45}>
          <CustomAutocomplete
            multiple
            filterSelectedOptions
            size="small"
            apiConfig={{
              url: USERS_ENDPOINT,
              queryParams: { is_active: "true", service_role: "true" },
            }}
            value={
              formData.external_service_selected
                ? [externalServiceOption, ...formData.assigned_to]
                : formData.assigned_to
            }
            // "External service" heads the technicians, unless already picked
            // or the search leaves it out.
            filterOptions={(options, state) => {
              const search = state.inputValue.trim().toLowerCase();
              const offerExternalService =
                canViewExternalServices &&
                !formData.external_service_selected &&
                (!search || String(externalServiceOption.full_name).toLowerCase().includes(search));
              return offerExternalService ? [externalServiceOption, ...options] : options;
            }}
            onChange={(_, newValue) => {
              const externalServiceSelected = newValue.some(isExternalServiceOption);
              setFormData((prev) => ({
                ...prev,
                assigned_to: newValue.filter((option) => !isExternalServiceOption(option)),
                external_service_selected: externalServiceSelected,
                external_service: externalServiceSelected ? prev.external_service : null,
              }));
            }}
            getOptionLabel={(option) => option.full_name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;
              if (!isExternalServiceOption(option)) {
                return (
                  <li key={key} {...optionProps}>
                    {option.full_name}
                  </li>
                );
              }
              return (
                <Box
                  component="li"
                  key={key}
                  {...optionProps}
                  sx={{ gap: 1, borderBottom: 1, borderColor: "divider" }}
                >
                  <ExternalServiceIcon fontSize="small" color="primary" />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {option.full_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("Hand the job over to an outside company")}
                    </Typography>
                  </Box>
                </Box>
              );
            }}
            renderValue={(selected, getItemProps) =>
              selected.map((option, index) => {
                const { key, ...itemProps } = getItemProps({ index });
                return isExternalServiceOption(option) ? (
                  <Chip
                    key={key}
                    {...itemProps}
                    size="small"
                    color="primary"
                    variant="outlined"
                    icon={<ExternalServiceIcon fontSize="small" />}
                    label={option.full_name}
                    disabled={!canViewExternalServices}
                  />
                ) : (
                  <Chip key={key} {...itemProps} size="small" label={option.full_name} />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Assigned to")}
                error={Boolean(getFieldError("assigned_to"))}
                helperText={getFieldError("assigned_to") || ""}
                className={styles.inputBase}
              />
            )}
          />
        </Box>

        {formData.external_service_selected && (
          <Box className={styles.field45}>
            <ExternalServiceAutocomplete
              value={formData.external_service}
              onChange={(externalService) =>
                setFormData((prev) => ({ ...prev, external_service: externalService }))
              }
              forEquipment={taskEquipmentIds}
              forClient={formData.client_id}
              disabled={!canViewExternalServices}
              error={externalServiceError}
              className={styles.inputBase}
            />
          </Box>
        )}
      </Box>

      {/* Description */}
      <TextField
        size="small"
        label={t("Description")}
        value={formData.description}
        onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
        fullWidth
        multiline
        minRows={3}
        error={Boolean(getFieldError("description"))}
        helperText={getFieldError("description") || ""}
        className={styles.inputBase}
      />

      </>
      )}

      {/* Tasks Section */}
      <Box className={styles.servicesSection}>
        <Box className={styles.servicesHeader}>
          <Typography variant="h6" className={styles.headerTypography}>
            {t("Tasks")}
          </Typography>
          <Button variant="outlined" startIcon={<Add />} size="small" onClick={addTaskRow}>
            {t("Add task")}
          </Button>
        </Box>

        {formData.tasks.map((task, index) => {
          // Equipment already chosen in the other task rows - hidden from this
          // row's picker so the same equipment can't be added twice.
          const equipmentSelectedInOtherRows = formData.tasks
            .filter((_, i) => i !== index)
            .map((otherRow) => otherRow.equipment)
            .filter(
              (equipmentId): equipmentId is string | number =>
                equipmentId !== null && equipmentId !== undefined
            );
          const selectedEquipment =
            autocompleteData.options["tasks.equipment"]?.find(
              (opt) => opt?.id !== undefined && String(opt.id) === String(task.equipment)
            ) || null;

          return (
          <Box
            key={`task-${index}`}
            className={styles.serviceBox}
          >
            <Box className={styles.serviceRowHeader}>
              <Typography variant="subtitle1" className={styles.subtitleTypography}>
                {t("Task")} #{index + 1}
              </Typography>
              <IconButton size="small" color="error" onClick={() => removeTaskRow(index)}>
                <Delete fontSize="small" />
              </IconButton>
            </Box>

            {/* Equipment (first row) */}
            <Box className={styles.formRow}>
              <Box className={styles.field45}>
                <EquipmentAutocomplete
                  size="small"
                  value={selectedEquipment as any}
                  onChange={(newValue) => {
                    if (newValue) {
                      setAutocompleteData((prev) => {
                        const current = prev.options["tasks.equipment"] || [];
                        const exists = current.some(
                          (opt) => opt?.id !== undefined && String(opt.id) === String(newValue.id)
                        );
                        if (exists) return prev;
                        return {
                          ...prev,
                          options: {
                            ...prev.options,
                            "tasks.equipment": [...current, newValue],
                          },
                        };
                      });
                      // Auto-fill client from equipment's active contract term, but only
                      // when no client is selected yet - do not override an existing choice
                      if (formData.client_id === null || formData.client_id === undefined) {
                        pendingClientLookupIdRef.current = newValue.id;
                        axios
                          .get(
                            `${CONTRACT_TERMS_ENDPOINT}?equipment=${newValue.id}&is_active=true`
                          )
                          .then(({ data }) => {
                            // Discard stale responses if a newer equipment was selected
                            if (pendingClientLookupIdRef.current !== newValue.id) return;
                            const results = Array.isArray(data?.results) ? data.results : data;
                            const firstTerm = Array.isArray(results) ? results[0] : null;
                            const clientId = firstTerm?.contract?.client_id ?? null;
                            if (clientId != null) {
                              // Update ref before state to prevent the client-change effect
                              // from clearing the just-selected equipment
                              previousClientIdRef.current = clientId;
                              axios
                                .get(`${CLIENTS_ENDPOINT}${clientId}/`)
                                .then(({ data: clientData }) => {
                                  if (clientData) {
                                    setSelectedClientOption(clientData);
                                    setFormData((prev) => ({ ...prev, client_id: clientId }));
                                  }
                                })
                                .catch(() => {});
                            }
                          })
                          .catch(() => {});
                      }
                    }
                    handleTaskFieldChange(index, "equipment", newValue ? newValue.id : null);
                  }}
                  clientId={formData.client_id !== null ? String(formData.client_id) : undefined}
                  filterUnattached={false}
                  onlyActiveContracts
                  excludeIds={equipmentSelectedInOtherRows}
                  hasNoAddress={formData.kind === "I"}
                  showAddEquipmentButton
                />
                <SelectedEquipmentDetails
                  equipment={selectedEquipment as any}
                  excludeJobId={jobId}
                />
              </Box>
            </Box>

            {/* Issues & Services (second row) */}
            <Box className={styles.formRow}>
              <Box className={styles.field45}>
                <CustomAutocomplete
                  multiple
                  // A picked issue leaves the list, as a picked technician does.
                  filterSelectedOptions
                  apiConfig={{
                    url: JOB_ISSUES_ENDPOINT,
                  }}
                  size="small"
                  value={task.issues}
                  onChange={(_, newValue) =>
                    handleTaskFieldChange(
                      index,
                      "issues",
                      newValue
                    )
                  }
                  addNew={
                    canAddJobIssue
                      ? { label: t("Add new issue"), onClick: () => setAddIssueTaskIndex(index) }
                      : null
                  }
                  getOptionLabel={(option) => option.title}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t("Issues")}
                      error={Boolean(getServiceFieldError(index, "issues"))}
                      helperText={getServiceFieldError(index, "issues") || ""}
                      className={styles.inputBase}
                    />
                  )}
                />
                <CustomModal
                  opened={addIssueTaskIndex === index}
                  setOpened={(opened) => setAddIssueTaskIndex(opened ? index : null)}
                  title={t("Add new issue")}
                  content={<JobIssueForm onSuccess={handleIssueCreated} />}
                />
              </Box>

              <Box className={styles.field45}>
                <TaskServicesField
                  value={task.services}
                  onChange={(services) => handleTaskFieldChange(index, "services", services)}
                  hoursWorked={inProgressHours(editedJobState.timeline)}
                  allowAddService
                  error={
                    getServiceFieldError(index, "services") ||
                    getServiceFieldError(index, "service_quantities") ||
                    undefined
                  }
                />
              </Box>
            </Box>

            {/* Task Description & Finished At */}
            <Box className={styles.formRow}>
              <TextField
                size="small"
                label={t("Task description")}
                value={task.description}
                onChange={(e) => handleTaskFieldChange(index, "description", e.target.value)}
                fullWidth
                multiline
                rows={2}
                error={Boolean(getServiceFieldError(index, "description"))}
                helperText={getServiceFieldError(index, "description") || ""}
                className={styles.inputBase}
              />
            </Box>

            {/* Task Attachments */}
            <Box className={styles.formRow}>
              <Box sx={{ flex: "1 1 100%" }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t("Attachments")}
                </Typography>
                <FileDropZoneField
                  onFileSelected={(file) => handleTaskAttachmentFile(index, file)}
                  uploading={uploadingTaskIndex === index}
                  disabled={uploadingTaskIndex !== null}
                />
                {task.attachments.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
                    {task.attachments.map((chip) => {
                      // Read-only rather than hidden: the technician still
                      // needs to see what was reported, they just cannot
                      // remove it. The backend refuses either way.
                      const readOnly = isServiceRole && chip.origin === "R";
                      const chipElement = (
                        <Chip
                          key={chip.id}
                          label={chip.label}
                          size="small"
                          clickable={Boolean(chip.url)}
                          onClick={
                            chip.url
                              ? (e) => {
                                  e.preventDefault();
                                  window.open(chip.url, "_blank", "noopener,noreferrer");
                                }
                              : undefined
                          }
                          onDelete={
                            readOnly
                              ? undefined
                              : (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  void handleRemoveTaskAttachment(index, chip);
                                }
                          }
                          deleteIcon={readOnly ? undefined : <Close fontSize="small" />}
                        />
                      );

                      return readOnly ? (
                        <Tooltip
                          key={chip.id}
                          title={t("Attached when the job was reported.")}
                        >
                          <span>{chipElement}</span>
                        </Tooltip>
                      ) : (
                        chipElement
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          );
        })}
      </Box>

      {/* Submit Button */}
      <Box className={styles.submitButtonContainer}>
        <Button variant="contained" color="success" disabled={loading} onClick={handleSubmit}>
          {isEditMode ? t("Update") : t("Submit")}
        </Button>
      </Box>

      <CustomModal
        opened={confirmFinishedEditOpened}
        setOpened={setConfirmFinishedEditOpened}
        title={t("Change a finished job?")}
        width={480}
        content={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Alert severity="error">{finishedJobWarning}</Alert>
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, flexWrap: "wrap" }}>
              <Button variant="outlined" onClick={() => setConfirmFinishedEditOpened(false)}>
                {t("Cancel")}
              </Button>
              <Button variant="contained" color="error" disabled={loading} onClick={submitJob}>
                {t("Save changes")}
              </Button>
            </Box>
          </Box>
        }
      />
    </Box>
  );
}
